import {Injectable} from '@angular/core';
import {ApiClientService} from './api-client.service';
import {Attachment, Email, RecipientMailingResult, ResidentInbox} from '../types/email-data.types';
import {UrlService} from './url.service';
import {Building, Group} from '../types/data.types';
import {FilterRecipients} from './filter-recipients';
import {EmptyRowService} from './empty-row.service';
import {AccountPermissionsService} from './account-permissions.service';
import {CanDeactivate} from '@angular/router';
import {ComposeEmailComponent} from '../views/email/compose-email/compose-email.component';
import {ConstantsService} from './constants.service';
import {Observable} from 'rxjs/Observable';
import {AuthenticationService} from './authentication.service';
import * as lodash from 'lodash';
import {BuildingApiService} from './api/building-api.service';
import {IBuilding} from '../types/api-response/building';
import {NpmiApiService} from './api/npmi-api.service';
import {forkJoin} from 'rxjs/observable/forkJoin';
import {INpmiPublicNoticePlaylist} from '../types/api-response/npmi-public-notice';
import {SendEmailRequest} from '../types/requests';
import {catchError} from 'rxjs/operators';
import {HttpErrorResponse} from '@angular/common/http';
import {FileUploadApiService} from './api/file-upload-api.service';

declare const toastr: any;
declare const swal: any;
declare const $: any;

@Injectable()
export class EmailService {
    isEmailViewModalShow = false;
    isMailingResultModalShow = false;
    isChoiceRecipientsModalShow = false;
    isViewAttachmentModalShow = false;
    showSearchInstructionsModal = false;
    isEmailSchedulingModalShow = false;
    isChooseCorporationModalShow = false;

    emailInMailingResultModal: Email;
    emailInViewModal: Email;
    emailInComposePage: Email;
    attachmentInViewAttachmentModal: Attachment = new Attachment;

    data: { entities: Array<Email | ResidentInbox>, total: number } = {
        entities: [],
        total: null
    };

    spinnerShow = false;
    showServiceUnavailable = false;
    buildings: IBuilding[] = [];
    buildingsShort: Array<any> = [];
    noticePlaylists: INpmiPublicNoticePlaylist[] = [];
    isNoticePlaylistsLoading: boolean = false;
    buildingsForRead: Building[] = [];
    buildingsForUpdate: Building[] = [];
    groups: Group[] = [];
    corporations: Array<any> = [];
    loadingCorporations = false;
    filterRecipients: FilterRecipients;
    serviceName: string = 'email_merge';
    public isUploadImageSpinnerShow = false;
    public maxSizeFile: number = 5; // in Mb;
    totalFileSize: number = 0;

    constructor(
        private buildingApi: BuildingApiService,
        private npmiApi: NpmiApiService,
        public api: ApiClientService,
        public urlService: UrlService,
        public emptyRowService: EmptyRowService,
        public account: AccountPermissionsService,
        private authenticationService: AuthenticationService,
        private uploadService: FileUploadApiService
    ) {
        this.api.onLogout.subscribe(() => {
            this.buildings = [];
            this.noticePlaylists = [];
        });
    }

    /**
     * Loads building details
     */
    getBuildings(): void {
        this.buildingApi.getBuildings({'per-page': 1000, 'order-by': 'name|asc'}, false).subscribe(result => {
            this.buildings = result.entities;
            this.getFilteredBuildings();
            this.initFilter();
        });
    }

    /**
     * Loads building details in short format
     */
    getBuildingsShort(): void {
        this.buildingApi.getBuildings({'per-page': 1000, 'order-by': 'name|asc'}, true).subscribe(result => {
            this.buildingsShort = this.account.getBuildingsForRead(result.entities, this.serviceName);
        });
    }

    /**
     * Loads notice playlists for buildings
     * which has postal code
     */
    public loadBuildingNotices() {
        const buildings = this.buildings.filter(building => building.postalCode);

        if (!buildings.length || this.noticePlaylists.length) {
            return;
        }

        this.isNoticePlaylistsLoading = true;
        const observables$ = buildings.map(building => this.npmiApi.getPublicNoticePlaylist(building.postalCode));

        forkJoin(observables$).subscribe((result) => {
            this.noticePlaylists = result.filter(notice => Array.isArray(notice.announcements) && notice.announcements.length);
            this.isNoticePlaylistsLoading = false;
        });
    }

    getGroups(): void {
        if (this.groups.length) return;
        const url = `/v1/group.json?per-page=1000`;
        this.api.get('domainBD', url).subscribe(
            res => {
                this.groups = [];
                const entities = JSON.parse(res._body).entities || [];
                this.groups = entities.map((item) => {
                    return {id: item.id, itemName: item.name, users: item.users};
                });
                this.initFilter();
            },
            error => this.api.errorHandler(error, 'Failed get groups'));
    }

    initFilter() {
        if (this.buildings.length && this.groups.length) {
            let residentsToRestore = [];
            if (this.emailInComposePage && this.filterRecipients && this.filterRecipients.residents.length)
                residentsToRestore = JSON.parse(JSON.stringify(this.filterRecipients.residents));
            this.filterRecipients = new FilterRecipients(this);
            if (residentsToRestore.length)
                this.filterRecipients.residents = residentsToRestore;
        }
    }

    isWaitingForGetAllMailings = false;
    getAllMailings(noSpinner?) {
        this.isWaitingForGetAllMailings = true;
        this.showServiceUnavailable = false;
        if (!noSpinner)
            this.spinnerShow = true;
        this.data.total = null;
        const selectedBuilding = JSON.parse(localStorage.getItem('residentselectedBuilding'));
        let sortedByBuilding = '';
        if (this.authenticationService.userIsResident && selectedBuilding) {
            sortedByBuilding = `&recipientSuite=${selectedBuilding.suite_id}`;
        }
        if (!this.authenticationService.userIsResident) {

            const baseUrl = '/api/v1/mailings-short.json';
            const queryParams = this.urlService.getQueryParamsString();
            const url = `${baseUrl}${queryParams}`.replace('.json&', '.json?');

            this.api.get('domainEmails', url).subscribe(
                res => {
                    this.isWaitingForGetAllMailings = false;
                    const body = JSON.parse(res._body);
                    this.data.entities = [];
                    this.data.total = body.total;
                    this.mailingsFactory(body.entities);
                    this.emptyRowService.fillEmptyRow();
                    this.spinnerShow = false;
                    if ($('.table')[0])
                        $('.table')[0].scrollIntoView();
                },
                error => {
                    this.isWaitingForGetAllMailings = false;
                    this.showServiceUnavailable = true;
                    this.spinnerShow = false;
                    this.api.errorHandler(error, 'Failed get Emails');
                }
            );
        } else {
            const baseUrl = '/v1/messages.json?';
            const queryParams = this.urlService.getQueryParamsString();
            const url = `${baseUrl}${queryParams}${sortedByBuilding}`;

            this.api.get('domainNotifications', url).subscribe(
                res => {
                    this.isWaitingForGetAllMailings = false;
                    this.showServiceUnavailable = false;
                    const body = JSON.parse(res._body);
                    this.data.entities = [];
                    this.data.total = body.total;
                    this.residentInboxFactory(body.entities);
                    this.emptyRowService.fillEmptyRow();
                    this.spinnerShow = false;
                },
                error => {
                    this.isWaitingForGetAllMailings = false;
                    this.showServiceUnavailable = true;
                    this.spinnerShow = false;
                    this.api.errorHandler(error, 'Failed get Notifications');
                }
            );
        }
    }

    mailingsFactoryCreate(item) {
        const email = new Email();

        if (item.id) email.id = item.id;
        if (item.sender) email.sender = item.sender;
        if (item.senderName) email.senderName = item.senderName;
        if (item.subject) email.subject = item.subject;
        if (item.replyTo) email.replyTo = item.replyTo;
        if (item.replyToName) email.replyToName = item.replyToName;
        if (item.message) email.message = item.message;
        if (item.pmName) email.pmName = item.pmName;
        if (item.pmEmail) email.pmEmail = item.pmEmail;
        if (item.pmMobilePhone) email.pmMobilePhone = item.pmMobilePhone;
        if (item.buildingName) email.buildingName = item.buildingName;
        if (item.buildingAddress) email.buildingAddress = item.buildingAddress;
        if (item.buildingId) email.buildingId = item.buildingId;
        if (item.sendMeCopy) email.sendMeCopy = item.sendMeCopy;
        if (item.status) email.status = item.status;
        if (item.scheduleDate) email.scheduleDate = item.scheduleDate.slice(0, -6) + '-00:00';
        if (item.filters) email.filtersList = JSON.parse(item.filters);
        if (item.recipientsFilter) email.recipientsFilter = item.recipientsFilter;
        if (item.recipients) email.recipients = item.recipients;
        if (item.createdAt) email.createdAt = item.createdAt;
        if (item.recipientsCount) email.recipientsCount = item.recipientsCount;
        if (item.sentCount) email.sentCount = item.sentCount;
        if (item.errorCount) email.errorCount = item.errorCount;
        if (item.attachments) {
            email.attachments = item.attachments.map(at => new Attachment(at.name, at.fileSize, at.path));
        }
        if (item.openedCount) email.openedCount = item.openedCount;
        if (item.clickedCount) email.clickedCount = item.clickedCount;
        email.isCanEdit = this.account.canUpdate(this.serviceName);
        email.isCanDelete = this.account.canDelete(this.serviceName);

        return email;
    }
    mailingsFactory(entities) {
        let items = [];
        for (const key in entities) {
            const item = entities[key];
            const email = this.mailingsFactoryCreate(item);
            this.data.entities.push(email);
        }
    }

    residentInboxFactory(entities) {
        for (const key in entities) {
            if (entities.hasOwnProperty(key)) {
                const item = entities[key];
                const inbox = new ResidentInbox();

                if (item.id) inbox.id = item.id;
                if (item.readAt) inbox.readAt = item.readAt;
                if (item.recipientSuite) inbox.recipientSuite = item.recipientSuite;
                if (item.subject) inbox.subject = item.subject;
                if (item.createdAt) inbox.createdAt = item.createdAt;

                this.data.entities.push(inbox);
            }
        }
    }

    showAttachment(attachment: Attachment) {
        if (attachment.isImage()) {
            this.attachmentInViewAttachmentModal = attachment;
            this.isViewAttachmentModalShow = true;
        } else if (attachment.isNeedViewer()) {
            attachment.openViewer();
        } else {
            attachment.downloadAttachment();
        }
    }

    showMailingResultModal(_email: Email) {
        this.spinnerShow = true;
        const url = `/api/v1/recipients.json?per-page=10000&emailMessageId=${_email.id}&order-by=status|asc`;

        this.api.getJson('domainEmails', url).subscribe(
            resBody => {
                const email = new Email();
                email.id = resBody.id;
                email.filtersList = _email.filtersList;
                email.subject = _email.subject;
                email.pmName = _email.pmName;
                email.createdAt = _email.createdAt;
                email.status = _email.status;
                email.recipientsCount = _email.recipientsCount;
                email.sentCount = _email.sentCount;
                email.openedCount = _email.openedCount;
                email.clickedCount = _email.clickedCount;

                for (const key in resBody.entities) {
                    const _resident = resBody.entities[key];
                    const r = new RecipientMailingResult();
                    r.id = _resident.id;
                    if (_resident.suiteName) r.suiteName = _resident.suiteName;
                    if (_resident.buildingName) r.buildingName = _resident.buildingName;
                    if (_resident.name) r.name = _resident.name;
                    if (_resident.type) r.type = _resident.type;
                    if (_resident.email) r.email = _resident.email;
                    if (_resident.status) r.status = _resident.status;

                    email.recipients.push(r);
                }

                this.emailInMailingResultModal = email;
                this.isMailingResultModalShow = true;
                this.spinnerShow = false;
            },
            error => this.api.errorHandler(error, 'Failed get Email for MailingResultModal')
        );
    }

    getFilteredBuildings() {
        this.buildingsForRead = this.account.getBuildingsForRead(this.buildings, this.serviceName);
        this.buildingsForUpdate = this.account.getBuildingsForUpdate(this.buildings, this.serviceName);
    }

    resetData() {
        this.data.entities = [];
        this.data.total = null;
    }

    getCorporations(): void {
        this.loadingCorporations = true;
        const url = `/v1/corporation_number.json?per-page=10000&order-by=name|asc`;
        this.api.get('domainBD', url).subscribe(
            res => {
                this.loadingCorporations = false;
                this.corporations = JSON.parse(res._body).entities || [];
                this.corporations = this.corporations.map(c => ({
                    ...c,
                    itemName: c.name
                }));
            },
            error => this.api.errorHandler(error, 'Failed get buildings')
        );
    }

    getAllOccupancyTypes(domain: string, url: string): Observable<any> {
        return this.buildingApi.getAllOccupancyTypes(domain, url);
    }

    sendEmail(data: SendEmailRequest) {
        return this.api.post('domainEmails', '/api/v1/mailings', data);
    }

    getCompanySettings(companyId) {
        const url = `/api/v1/company_service_settings/${companyId}`;
        return this.api.getJson('domainEmails', url);
    }

    getCorporationSettings() {
        const url = `/api/v1/corporation_number_service_settings`;
        return this.api.getJson('domainEmails', url);
    }

    uploadFile(files, summernote) {
        if (this.totalFileSize + files[0].size > this._toBytes(this.maxSizeFile)) {
            toastr.error('Image size exceeds the maximum allowed 5 MB.');
        } else {
            this.totalFileSize += files[0].size;
            this.isUploadImageSpinnerLayout(true);
            this.uploadService.fileUpload(files[0]).pipe(
                catchError((error: HttpErrorResponse) => {
                    toastr.error(error.statusText, 'File upload failed');
                    return Observable.throw(error);
                })
            ).subscribe(file => {
                summernote.summernote('insertImage', file.link, (image) => {
                    image.css('width', '100%');
                    image.attr('data-filename', file.name);
                    image.attr('data-filesize', file.size);
                    this.isUploadImageSpinnerLayout(false);
                });
            });
        }
    }

    deleteFile(target, summernote) {
        summernote.summernote('editor.insertText', ' ');
        const size: number = Number(target[0].getAttribute('data-filesize'));
        this.totalFileSize -= size;
    }

    private _toBytes(size: number): number {
        return size * 1024 ** 2;
    }

    isUploadImageSpinnerLayout(isSpinnerShow: boolean): void {
        this.isUploadImageSpinnerShow = isSpinnerShow;
        const classBody: string = 'modal-open';
        const classBodyOverlay: string = 'body-backdrop';
        if (this.isUploadImageSpinnerShow) {
            document.body.classList.add(classBody);
            document.body.classList.add(classBodyOverlay);
        } else {
            document.body.classList.remove(classBody);
            document.body.classList.remove(classBodyOverlay);
        }
    }
}

@Injectable()
export class ConfirmDeactivateGuard implements CanDeactivate<ComposeEmailComponent> {
    canDeactivate(target: ComposeEmailComponent): Observable<boolean> | boolean {
        if ((target.data.subject || target.data.message || (target.data.attachments && target.data.attachments.length > 0) ||
            (target.getResidentData() && target.getResidentData().length > 0) || target.data.replyTo || target.data.replyToName) && !lodash.isEqual(target.initialState, target.data)) {
            return new Observable(observer => {
                swal(ConstantsService.getSwalConfig('Close without saving?', 'Do you want to delete unsaved data?'),
                    (value) => {
                        observer.next(value);
                    }
                );
            });
        } else
            return true;
    }

}



// WEBPACK FOOTER //
// /app/src/app/services/email.service.ts